import type { Paginated, SecretariaSummary } from '@obras/shared';
import { useQuery } from '@tanstack/react-query';
import { Landmark, Pencil, Plus, Search } from 'lucide-react';
import { useEffect, useState } from 'react';

import { AppShell, PageHeader } from '../components/AppShell.js';
import { Badge } from '../components/ui/Badge.js';
import { Button } from '../components/ui/Button.js';
import { Card } from '../components/ui/Card.js';
import { SearchInput } from '../components/ui/Field.js';
import { EmptyState, ErrorState, Skeleton } from '../components/ui/Feedback.js';
import { SecretariaFormModal } from '../features/secretarias/SecretariaFormModal.js';
import { api, queryString } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

const ICON = { strokeWidth: 1.5 } as const;

/** Espera a digitação parar antes de consultar — igual às demais listagens. */
function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

/**
 * As secretarias demandantes do município.
 *
 * A coluna de obras é o que justifica a tela existir: é dela que sai "quantas
 * obras a Educação tem em andamento", pergunta que antes só se respondia lendo
 * o nome de cada obra da lista.
 */
export function SecretariasPage() {
  const { can } = useAuth();
  const [formOpen, setFormOpen] = useState(false);
  const [editando, setEditando] = useState<SecretariaSummary | null>(null);
  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounced(search);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['secretarias', 'list', debouncedSearch],
    queryFn: ({ signal }) =>
      api.get<Paginated<SecretariaSummary>>(
        `/secretarias${queryString({ perPage: 100, search: debouncedSearch || undefined })}`,
        signal,
      ),
    placeholderData: (anterior) => anterior,
  });

  const podeEditar = can('gestor');

  return (
    <AppShell>
      <PageHeader
        title="Secretarias"
        description="Órgãos internos que demandam obras, com o total de obras de cada um."
        action={
          podeEditar ? (
            <Button
              variant="primary"
              icon={<Plus {...ICON} size={16} />}
              onClick={() => setFormOpen(true)}
            >
              Nova secretaria
            </Button>
          ) : null
        }
      />

      <div className="mb-4 flex justify-end">
        <div className="relative w-full sm:w-80">
          <Search
            {...ICON}
            size={16}
            className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <SearchInput
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por nome ou sigla"
            aria-label="Buscar secretarias"
            className="pr-3 pl-9"
          />
        </div>
      </div>

      {isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : 'Erro desconhecido.'}
          onRetry={() => void refetch()}
        />
      ) : isPending ? (
        <Card className="p-5">
          <div className="flex flex-col gap-3">
            {Array.from({ length: 4 }, (_, index) => (
              <Skeleton key={index} className="h-12" />
            ))}
          </div>
        </Card>
      ) : data.items.length === 0 ? (
        <Card>
          <EmptyState
            icon={Landmark}
            title="Nenhuma secretaria cadastrada"
            description="Cadastre as secretarias para registrar qual delas demandou cada obra e acompanhar o portfólio por órgão."
            action={
              podeEditar ? (
                <Button
                  variant="primary"
                  icon={<Plus {...ICON} size={16} />}
                  onClick={() => setFormOpen(true)}
                >
                  Nova secretaria
                </Button>
              ) : null
            }
          />
        </Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left">
            <caption className="sr-only">Secretarias demandantes</caption>
            <thead className="bg-muted">
              <tr className="text-[11px] font-medium tracking-[0.04em] text-muted-foreground uppercase">
                <th scope="col" className="px-5 py-3">
                  Secretaria
                </th>
                <th scope="col" className="px-5 py-3">
                  Sigla
                </th>
                <th scope="col" className="px-5 py-3 text-right">
                  Obras
                </th>
                <th scope="col" className="px-5 py-3">
                  Situação
                </th>
                <th scope="col" className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {data.items.map((secretaria) => (
                <tr
                  key={secretaria.id}
                  /* Sem realce ao passar o mouse: a linha não é clicável. */
                  className="border-t border-border"
                >
                  <td className="px-5 py-3">
                    <span className="block font-medium">{secretaria.name}</span>
                    {secretaria.description ? (
                      <span className="block text-[13px] text-muted-foreground">
                        {secretaria.description}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-5 py-3 font-mono text-[13px]">{secretaria.acronym ?? '—'}</td>
                  <td className="tabular px-5 py-3 text-right font-medium">
                    {secretaria.obrasCount}
                  </td>
                  <td className="px-5 py-3">
                    <Badge tone={secretaria.status === 'ativa' ? 'success' : 'muted'}>
                      {secretaria.status === 'ativa' ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </td>
                  <td className="px-5 py-3 text-right">
                    {podeEditar ? (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="size-8"
                        title={`Editar ${secretaria.name}`}
                        aria-label={`Editar ${secretaria.name}`}
                        onClick={() => setEditando(secretaria)}
                      >
                        <Pencil {...ICON} size={16} aria-hidden />
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <SecretariaFormModal
        open={formOpen || editando !== null}
        editando={editando}
        onClose={() => {
          setFormOpen(false);
          setEditando(null);
        }}
      />
    </AppShell>
  );
}
